import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  layoutConceptGraph,
  missingPrerequisites,
  pathTo,
  type Concept,
  type StrandId,
} from '@yuan-math/curriculum';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { graphPositions } from '../components/conceptLayout.ts';
import { MasteryBar } from '@yuan-math/ui';

/**
 * The Math Map (§5).
 *
 * This is the product, drawn: every concept, every prerequisite edge, and where
 * the student stands in it. Layout comes from `layoutConceptGraph` in the
 * curriculum package — the view does no graph reasoning of its own, it only
 * turns layers and slots into coordinates.
 *
 * The picture alone is not enough to navigate 285 concepts. Beneath it sits an
 * index of exactly what the map is showing, in the same top-to-bottom order, so
 * a concept can be *found* by reading rather than by hunting for a small box —
 * and picking a row zooms the map onto that concept. The two halves are one
 * view of one selection, never two independent widgets.
 */

const NODE_WIDTH = 168;
const NODE_HEIGHT = 60;
const COLUMN_GAP = 34;
// Enough room for the bezier between two rows to read as a curve, and no more.
// The old 92 spent more height on gaps than on concepts, which cost a third of
// the rows a strand can show at once inside the canvas.
const ROW_GAP = 70;

/**
 * The floor on fitting-to-width. Every strand now compacts to at most six
 * columns and fits at full size, but all 285 concepts together are eighteen
 * columns wide and fitting *those* to a window opened the map at 4px type.
 * Legibility wins: a map that must be scrolled beats one that cannot be read,
 * and the index below is how a particular concept gets found.
 */
const MIN_FIT_ZOOM = 0.7;
/** What "zoom to this concept" means: comfortably readable, not merely fitted. */
const FOCUS_ZOOM = 0.9;
/** The range of the zoom control, and the clamp every zoom is put through. */
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 1.6;
/** The canvas' own padding, which is not room the drawing can use. */
const CANVAS_PADDING = 32;

const clampZoom = (value: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

type NodeStatus = 'mastered' | 'in-progress' | 'ready' | 'locked' | 'planned';
type Relation = 'selected' | 'prerequisite' | 'dependent' | 'route' | null;
type TableScope = 'view' | 'related';

export function MathMapPage(): JSX.Element {
  const { profile, masteredConceptIds, knownConceptIds, masteryOf } = useLearner();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showPlanned, setShowPlanned] = useState(true);
  const [strandFilter, setStrandFilter] = useState<string>('all');
  const [tableScope, setTableScope] = useState<TableScope>('view');
  const [search, setSearch] = useState('');
  const [zoom, setZoom] = useState(0.6);
  const canvasRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);

  /** The concepts the filters admit — the table's "everything shown" scope. */
  const filtered = useMemo(() => {
    return registry.concepts.filter((concept) => {
      if (!showPlanned && concept.status === 'planned') return false;
      if (strandFilter !== 'all' && concept.strand !== strandFilter) return false;
      return true;
    });
  }, [showPlanned, strandFilter]);

  /**
   * What the map draws. A filter is a lens, not a wall: whatever is selected and
   * everything touching it directly stays on the map even when the strand filter
   * would hide it, because following a prerequisite out of the strand is exactly
   * the relationship a student came here to see.
   */
  const visible = useMemo(() => {
    const ids = new Set(filtered.map((concept) => concept.id));
    if (selectedId) {
      ids.add(selectedId);
      for (const id of registry.graph.prerequisites.get(selectedId) ?? []) ids.add(id);
      for (const id of registry.graph.dependents.get(selectedId) ?? []) ids.add(id);
    }
    return ids;
  }, [filtered, selectedId]);

  // Laid out over the visible set, so a single strand is drawn compactly rather
  // than as a handful of nodes keeping their slots in a two-hundred-wide row.
  const layout = useMemo(() => layoutConceptGraph(registry.graph, visible), [visible]);

  const positions = useMemo(
    () =>
      graphPositions(layout, {
        nodeWidth: NODE_WIDTH,
        nodeHeight: NODE_HEIGHT,
        columnGap: COLUMN_GAP,
        rowGap: ROW_GAP,
      }),
    [layout],
  );

  const statusOf = (concept: Concept): NodeStatus => {
    if (concept.status === 'planned') return 'planned';
    if (masteredConceptIds.has(concept.id)) return 'mastered';
    const started = (profile?.concepts[concept.id]?.attempts ?? 0) > 0;
    if (started) return 'in-progress';
    // Readiness is asked of what the student knows, not of what they have
    // proved: a learner the placement test put at Grade 12 has proved nothing
    // yet, and reading the map off mastery alone drew their entire graph locked.
    return missingPrerequisites(registry.graph, concept.id, knownConceptIds).length === 0 ? 'ready' : 'locked';
  };

  /** The route from what the student knows to the concept they clicked. */
  const highlightPath = useMemo(() => {
    if (!selectedId) return new Set<string>();
    return new Set(pathTo(registry.graph, selectedId, knownConceptIds));
  }, [selectedId, knownConceptIds]);

  const selected = selectedId ? registry.concept(selectedId) : undefined;

  const relationOf = (conceptId: string): Relation => {
    if (!selectedId) return null;
    if (conceptId === selectedId) return 'selected';
    if ((registry.graph.prerequisites.get(selectedId) ?? []).includes(conceptId)) return 'prerequisite';
    if ((registry.graph.dependents.get(selectedId) ?? []).includes(conceptId)) return 'dependent';
    return highlightPath.has(conceptId) ? 'route' : null;
  };

  /** Rows for the index, in the map's own top-to-bottom order. */
  const rows = useMemo(() => {
    const slot = new Map(layout.nodes.map((node, index) => [node.id, index]));
    const scoped =
      tableScope === 'related' && selectedId
        ? [
            selectedId,
            ...(registry.graph.prerequisites.get(selectedId) ?? []),
            ...(registry.graph.dependents.get(selectedId) ?? []),
          ]
            .map((id) => registry.concept(id))
            .filter((concept): concept is Concept => concept !== undefined)
        : filtered;

    const needle = search.trim().toLowerCase();
    return scoped
      .filter((concept) => {
        if (!needle) return true;
        return (
          concept.title.toLowerCase().includes(needle) || concept.summary.toLowerCase().includes(needle)
        );
      })
      .sort((a, b) => (slot.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (slot.get(b.id) ?? Number.MAX_SAFE_INTEGER));
  }, [filtered, layout.nodes, search, selectedId, tableScope]);

  /** Select a concept and bring the map in close enough to read it. */
  const focusConcept = (conceptId: string): void => {
    setSelectedId(conceptId);
    setZoom((current) => Math.max(current, FOCUS_ZOOM));
  };

  /**
   * Show the whole drawing at once, however small that has to be. This is the
   * one place `MIN_FIT_ZOOM` does not apply: automatic fitting must not silently
   * hand back unreadable type, but a student who asks for the overview has
   * decided that seeing the shape of it is worth the small labels.
   */
  const fitEverything = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setZoom(
      clampZoom(
        Math.min(
          1,
          (canvas.clientWidth - CANVAS_PADDING) / positions.width,
          (canvas.clientHeight - CANVAS_PADDING) / positions.height,
        ),
      ),
    );
  };

  // Fit the graph to the available width whenever the *filters* change. Keyed on
  // the filters rather than on the layout, because selecting a concept also
  // changes the layout and must not throw away a zoom the student chose.
  const fitKey = `${strandFilter}:${showPlanned}`;
  const widthRef = useRef(positions.width);
  widthRef.current = positions.width;
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const available = canvas.clientWidth - CANVAS_PADDING;
    setZoom(Math.min(1, Math.max(MIN_FIT_ZOOM, available / widthRef.current)));
  }, [fitKey]);

  // Keep the view centred horizontally, since layers are centred.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.scrollLeft = (canvas.scrollWidth - canvas.clientWidth) / 2;
  }, [zoom, strandFilter, showPlanned]);

  // Bring the selected concept into view rather than making the student hunt —
  // on the map, and in the index below it.
  useEffect(() => {
    if (!selectedId) return;
    const canvas = canvasRef.current;
    const position = positions.map.get(selectedId);
    if (canvas && position) {
      canvas.scrollTo({
        left: position.x * zoom - canvas.clientWidth / 2 + (NODE_WIDTH * zoom) / 2,
        top: position.y * zoom - canvas.clientHeight / 2,
        behavior: 'smooth',
      });
    }
    tableBodyRef.current
      ?.querySelector(`tr[data-concept-id="${selectedId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedId, positions.map, zoom]);

  const strandTitle = strandFilter === 'all' ? null : registry.strand(strandFilter as StrandId)?.title;

  return (
    <div className="page page--wide">
      <header className="map-header">
        <div>
          <h1 className="page__title">The Math Map</h1>
          <p className="page__lead">
            Every concept, and what it rests on. Choose a strand to draw just that part of the mathematics, then pick a
            concept — on the map or in the list beneath it — to see its prerequisites, what it unlocks, and the shortest
            route to it from where you are now.
          </p>
        </div>

        <div className="map-controls">
          <label className="map-control">
            <span>Strand</span>
            <select value={strandFilter} onChange={(event) => setStrandFilter(event.target.value)}>
              <option value="all">All strands</option>
              {registry.strands.map((strand) => (
                <option key={strand.id} value={strand.id}>
                  {strand.title}
                </option>
              ))}
            </select>
          </label>
          <label className="map-control map-control--check">
            <input type="checkbox" checked={showPlanned} onChange={(event) => setShowPlanned(event.target.checked)} />
            <span>Show what is coming</span>
          </label>
          <label className="map-control">
            <span>Zoom {Math.round(zoom * 100)}%</span>
            <input
              type="range"
              min={Math.round(MIN_ZOOM * 100)}
              max={Math.round(MAX_ZOOM * 100)}
              value={Math.round(zoom * 100)}
              onChange={(event) => setZoom(clampZoom(Number(event.target.value) / 100))}
              aria-label="Zoom the map"
            />
          </label>
          <button type="button" className="btn btn--ghost btn--sm" onClick={fitEverything}>
            Fit to view
          </button>
        </div>
      </header>

      <div className="map-legend" role="list">
        {(['mastered', 'in-progress', 'ready', 'locked', 'planned'] as NodeStatus[]).map((status) => (
          <span key={status} role="listitem" className="map-legend__item">
            <span className={`map-legend__swatch node--${status}`} aria-hidden="true" />
            {legendLabel(status)}
          </span>
        ))}
      </div>

      <div className="map-canvas" role="region" aria-label="Concept map" tabIndex={0} ref={canvasRef}>
        <svg
          viewBox={`0 0 ${positions.width} ${positions.height}`}
          width={positions.width * zoom}
          height={positions.height * zoom}
          className="map-svg"
        >
          {/* Edges first, so nodes always sit on top of them. */}
          <g className="map-edges">
            {registry.concepts.flatMap((concept) =>
              concept.prerequisites.map((prereqId) => {
                if (!visible.has(concept.id) || !visible.has(prereqId)) return null;
                const from = positions.map.get(prereqId);
                const to = positions.map.get(concept.id);
                if (!from || !to) return null;
                const highlighted = highlightPath.has(concept.id) && highlightPath.has(prereqId);
                const x1 = from.x + NODE_WIDTH / 2;
                const y1 = from.y + NODE_HEIGHT;
                const x2 = to.x + NODE_WIDTH / 2;
                const y2 = to.y;
                const midY = (y1 + y2) / 2;
                return (
                  <path
                    key={`${prereqId}->${concept.id}`}
                    d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                    className={`map-edge ${highlighted ? 'map-edge--active' : ''}`}
                    fill="none"
                  />
                );
              }),
            )}
          </g>

          <g className="map-nodes">
            {registry.concepts.map((concept) => {
              if (!visible.has(concept.id)) return null;
              const position = positions.map.get(concept.id);
              if (!position) return null;
              const status = statusOf(concept);
              const onPath = highlightPath.has(concept.id);
              const mastery = masteryOf(concept.id);

              return (
                <g
                  key={concept.id}
                  transform={`translate(${position.x}, ${position.y})`}
                  className={`map-node node--${status} ${onPath ? 'map-node--on-path' : ''} ${selectedId === concept.id ? 'map-node--selected' : ''}`}
                  onClick={() => (concept.id === selectedId ? setSelectedId(null) : focusConcept(concept.id))}
                  role="button"
                  tabIndex={0}
                  aria-label={`${concept.title}, ${legendLabel(status)}, ${Math.round(mastery.overall * 100)}% mastery`}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      if (concept.id === selectedId) setSelectedId(null);
                      else focusConcept(concept.id);
                    }
                  }}
                >
                  <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={8} className="map-node__box" />
                  {mastery.overall > 0 ? (
                    <rect y={NODE_HEIGHT - 4} width={NODE_WIDTH * mastery.overall} height={4} rx={2} className="map-node__mastery" />
                  ) : null}
                  <foreignObject x={10} y={8} width={NODE_WIDTH - 20} height={NODE_HEIGHT - 16}>
                    <span className="map-node__title">{concept.title}</span>
                  </foreignObject>
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {selected ? (
        <ConceptPanel conceptId={selected.id} pathLength={highlightPath.size} onClose={() => setSelectedId(null)} />
      ) : (
        <p className="map-hint">
          Pick a concept on the map, or from the list below. {profile ? '' : 'Sign in to see your own mastery drawn onto the map.'}
        </p>
      )}

      <section className="concept-index" aria-label="Concepts on the map">
        <header className="concept-index__header">
          <div>
            <h2 className="concept-index__title">{strandTitle ?? 'Every concept'}</h2>
            <p className="concept-index__count">
              {rows.length} {rows.length === 1 ? 'concept' : 'concepts'}
              {tableScope === 'related' && selected ? ` related to ${selected.title}` : ''}
              {search.trim() ? ` matching “${search.trim()}”` : ''}. Choose one to zoom the map to it.
            </p>
          </div>

          <div className="concept-index__tools">
            <div className="segmented" role="group" aria-label="Which concepts to list">
              <button
                type="button"
                className={`segmented__option ${tableScope === 'view' ? 'segmented__option--on' : ''}`}
                aria-pressed={tableScope === 'view'}
                onClick={() => setTableScope('view')}
              >
                {strandTitle ? 'All in strand' : 'All concepts'}
              </button>
              <button
                type="button"
                className={`segmented__option ${tableScope === 'related' ? 'segmented__option--on' : ''}`}
                aria-pressed={tableScope === 'related'}
                disabled={!selected}
                onClick={() => setTableScope('related')}
              >
                {selected ? `Related to ${selected.title}` : 'Related'}
              </button>
            </div>
            <label className="map-control">
              <span>Find</span>
              <input
                type="search"
                className="concept-index__search"
                value={search}
                placeholder="Search concepts"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          </div>
        </header>

        {rows.length === 0 ? (
          <p className="map-hint">Nothing here matches. Clear the search, or choose another strand.</p>
        ) : (
          <div className="concept-index__scroll">
            <table className="concept-table">
              <caption className="visually-hidden">
                Concepts currently drawn on the map, in the order they are taught
              </caption>
              <thead>
                <tr>
                  <th scope="col">Concept</th>
                  <th scope="col">Usually taught</th>
                  <th scope="col">Status</th>
                  <th scope="col">Mastery</th>
                  <th scope="col" className="concept-table__num">Builds on</th>
                  <th scope="col" className="concept-table__num">Leads to</th>
                </tr>
              </thead>
              <tbody ref={tableBodyRef}>
                {rows.map((concept) => {
                  const status = statusOf(concept);
                  const relation = relationOf(concept.id);
                  const mastery = masteryOf(concept.id);
                  return (
                    <tr
                      key={concept.id}
                      data-concept-id={concept.id}
                      className={`concept-table__row ${selectedId === concept.id ? 'concept-table__row--selected' : ''}`}
                    >
                      <th scope="row" className="concept-table__name">
                        <button
                          type="button"
                          className="concept-table__pick"
                          aria-pressed={selectedId === concept.id}
                          onClick={() => focusConcept(concept.id)}
                        >
                          {concept.title}
                        </button>
                        <span className="concept-table__meta">
                          {registry.strand(concept.strand)?.title}
                          {relation ? <span className={`relation relation--${relation}`}>{relationLabel(relation)}</span> : null}
                        </span>
                      </th>
                      <td>{concept.levels.map((id) => registry.level(id)?.shortTitle ?? id).join(', ') || '—'}</td>
                      <td>
                        <span className={`status-pill node--${status}`}>
                          <span className="status-pill__dot" aria-hidden="true" />
                          {legendLabel(status)}
                        </span>
                      </td>
                      <td className="concept-table__mastery">
                        <MasteryBar value={mastery.overall} size="sm" />
                      </td>
                      <td className="concept-table__num">{(registry.graph.prerequisites.get(concept.id) ?? []).length}</td>
                      <td className="concept-table__num">{(registry.graph.dependents.get(concept.id) ?? []).length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ConceptPanel({
  conceptId,
  pathLength,
  onClose,
}: {
  conceptId: string;
  pathLength: number;
  onClose: () => void;
}): JSX.Element | null {
  const { masteryOf, knownConceptIds } = useLearner();
  const concept = registry.concept(conceptId);
  if (!concept) return null;

  const prerequisites = concept.prerequisites.map((id) => registry.concept(id)).filter(Boolean);
  const unlocks = (registry.graph.dependents.get(conceptId) ?? []).map((id) => registry.concept(id)).filter(Boolean);
  const lessons = registry.lessonsForConcept(conceptId);
  const mastery = masteryOf(conceptId);
  const missing = missingPrerequisites(registry.graph, conceptId, knownConceptIds);

  return (
    <aside className="concept-panel" aria-label={`${concept.title} details`}>
      <div className="concept-panel__header">
        <div>
          <p className="concept-panel__strand">{registry.strand(concept.strand)?.title}</p>
          <h2 className="concept-panel__title">{concept.title}</h2>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Close details">
          Close
        </button>
      </div>

      <p className="concept-panel__summary">{concept.summary}</p>

      {concept.status === 'available' ? (
        <MasteryBar value={mastery.overall} label="Mastery" />
      ) : (
        <p className="concept-panel__planned">
          This concept is on the map but not yet written. It is here so you can see where the mathematics leads.
        </p>
      )}

      <dl className="concept-panel__facts">
        <div>
          <dt>Usually taught</dt>
          <dd>{concept.levels.map((id) => registry.level(id)?.shortTitle ?? id).join(', ') || '—'}</dd>
        </div>
        <div>
          <dt>Lessons</dt>
          <dd>{lessons.length}</dd>
        </div>
        <div>
          <dt>Builds on</dt>
          <dd>{prerequisites.length}</dd>
        </div>
        <div>
          <dt>Unlocks</dt>
          <dd>{unlocks.length}</dd>
        </div>
      </dl>

      {prerequisites.length > 0 ? (
        <section className="concept-panel__section">
          <h3>Builds on</h3>
          <ul className="chip-list">
            {prerequisites.map((prereq) => (
              <li key={prereq!.id}>
                <Link to={`/concepts/${prereq!.id}`} className={`chip ${missing.includes(prereq!.id) ? 'chip--missing' : 'chip--met'}`}>
                  {prereq!.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {unlocks.length > 0 ? (
        <section className="concept-panel__section">
          <h3>Leads to</h3>
          <ul className="chip-list">
            {unlocks.slice(0, 8).map((next) => (
              <li key={next!.id}>
                <Link to={`/concepts/${next!.id}`} className="chip">
                  {next!.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {pathLength > 1 ? (
        <p className="concept-panel__path">
          {pathLength} concepts stand between you and this one — highlighted on the map above.
        </p>
      ) : null}

      <div className="button-row">
        <Link to={`/concepts/${conceptId}`} className="btn btn--secondary btn--sm">
          Concept details
        </Link>
        {lessons[0] ? (
          <Link to={`/lessons/${lessons[0].id}`} className="btn btn--primary btn--sm">
            Open the lesson
          </Link>
        ) : null}
      </div>
    </aside>
  );
}

function legendLabel(status: NodeStatus): string {
  switch (status) {
    case 'mastered':
      return 'Mastered';
    case 'in-progress':
      return 'In progress';
    case 'ready':
      return 'Ready to start';
    case 'locked':
      return 'Needs prerequisites';
    case 'planned':
      return 'Coming soon';
  }
}

function relationLabel(relation: Exclude<Relation, null>): string {
  switch (relation) {
    case 'selected':
      return 'Selected';
    case 'prerequisite':
      return 'Comes before';
    case 'dependent':
      return 'Comes after';
    case 'route':
      return 'On your route';
  }
}
